import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Button, OverlayTrigger, Table, Tooltip } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { decrypt, history, isMatchingKeyPair } from "../../_helpers";
import { useDispatch, useSelector } from "react-redux";
import { vesselServices } from "../services/vessels.services";
import * as moment from "moment";
import RemoveVessel from "./RemoveVessel";
import CopyVessel from "./CopyVessel";
import TransferVessel from "./TransferVessel";
import AssociateUserVessel from "./AssociateUserVessel";
import { PrivateKey } from "../../Contract/_components/PrivateKey";
import { alertActions } from "../../_actions";
import { alertConstants } from "../../_constants";

const initModal = {
  show: false,
  vesselId: null,
  isAssociated: null,
  vesselPublicKey: null,
  vesselSecretKey: null,
};

export default function VesselsList() {
  const { t } = useTranslation();
  const user = useSelector((state) => state.authentication.user);
  const [vessels, setVessels] = useState([]);

  const [showDeleteModal, setShowDeleteModal] = useState(initModal);
  const [showCopyModal, setShowCopyModal] = useState(initModal);
  const [showTransferModal, setShowTransferModal] = useState(initModal);
  const [showAssociateModal, setShowAssociateModal] = useState(initModal);
  const privateKey = useSelector((state) => state.cryptKeys.privateKey);
  const [pkValid, setPkValid] = useState(false);
  const dispatch = useDispatch();

  const safeDecrypt = (encryptedValue, publicKey, privateKey) => {
    try {
      return decrypt(encryptedValue, publicKey, privateKey);
    } catch (error) {
      console.error("Erreur de déchiffrement :", error, {
        encryptedValue,
        publicKey,
        privateKey,
      });
      return "🔒";
    }
  };
  useEffect(() => {
    const fetchVessels = async () => {
      if (privateKey && pkValid) {
        try {
          const response = await vesselServices?.getVesselsByAuthor(user?.id);
          setVessels(response);
        } catch (err) {
          console.error("Erreur lors de la récupération des vessels :", err);
        }
      }
    };

    fetchVessels();
  }, [privateKey, pkValid, user?.id]);
  const handleEdit = (id) => {
    history.push(`/vessels/edit/${id}`);
  };

  const isPrivateKeyValid = useCallback(() => {
    if (privateKey !== "" && user?.publicKey) {
      const checkPK = isMatchingKeyPair(privateKey, user?.publicKey);
      if (!checkPK) {
        setPkValid(false);
        dispatch(
          alertActions.setAlert(t("Incorrect key"), alertConstants.ERROR)
        );
      } else {
        setPkValid(true);
      }
    }
  }, [privateKey, user, dispatch, t]);

  useEffect(() => {
    isPrivateKeyValid();
  }, [isPrivateKeyValid]);

  const toggleDeleteModal = (
    id,
    sheetName,
    isAssociated,
    vesselPublicKey,
    vesselSecretKey
  ) => {
    const vesselId = !showDeleteModal.show === true ? id : null;
    const sheetNameValue = !showDeleteModal.show === true ? sheetName : null;
    setShowDeleteModal({
      vesselId,
      sheetName: sheetNameValue,
      show: !showDeleteModal.show,
      vesselPublicKey,
      vesselSecretKey,
    });
  };

  const toggleCopyModal = (
    id,
    vessel,
    isAssociated,
    vesselPublicKey,
    vesselSecretKey
  ) => {
    const vesselId = !showCopyModal.show === true ? id : null;
    setShowCopyModal({
      vesselId,
      vessel,
      show: !showCopyModal.show,
      vesselPublicKey,
      vesselSecretKey,
      isAssociated,
    });
  };

  const toggleTransferModal = (
    id,
    sheetName,
    isAssociated,
    vesselPublicKey,
    vesselSecretKey
  ) => {
    const vesselId = !showTransferModal.show === true ? id : null;
    const sheetNameValue = !showTransferModal.show === true ? sheetName : null;
    setShowTransferModal({
      vesselId,
      sheetName: sheetNameValue,
      show: !showTransferModal.show,
      vesselPublicKey,
      vesselSecretKey,
      isAssociated,
    });
  };

  const toggleAssociateModal = (vesselId) => {
    const vesselIdValue = !showAssociateModal.show === true ? vesselId : null;
    setShowAssociateModal({
      show: !showAssociateModal.show,
      vesselId: vesselIdValue,
    });
  };

  return (
    <>
      <PrivateKey text="To view or add vessels, please set your private key" />

      {privateKey && pkValid && (
        <>
          <RemoveVessel
            vesselId={showDeleteModal?.vesselId}
            toggle={toggleDeleteModal}
            show={showDeleteModal?.show}
            setVessels={setVessels}
            userId={user?.id}
            sheetName={showDeleteModal?.sheetName}
            publicKey={
              showDeleteModal?.isAssociated
                ? showDeleteModal?.vesselPublicKey
                : user?.publicKey
            }
            secretKey={
              showDeleteModal?.isAssociated
                ? showDeleteModal?.vesselSecretKey
                : privateKey
            }
          />

          <CopyVessel
            vesselId={showCopyModal?.vesselId}
            toggle={toggleCopyModal}
            show={showCopyModal?.show}
            vessel={showCopyModal?.vessel}
            setVessels={setVessels}
            userId={user?.id}
            publicKey={
              showCopyModal?.isAssociated
                ? showCopyModal?.vesselPublicKey
                : user?.publicKey
            }
            secretKey={
              showCopyModal?.isAssociated
                ? showCopyModal?.vesselSecretKey
                : privateKey
            }
            userPrivateKey={privateKey}
            userPublicKey={user?.publicKey}
          />

          <TransferVessel
            vesselId={showTransferModal?.vesselId}
            toggle={toggleTransferModal}
            show={showTransferModal?.show}
            sheetName={showTransferModal?.sheetName}
            setVessels={setVessels}
            userId={user?.id}
            publicKey={
              showTransferModal?.isAssociated
                ? showTransferModal?.vesselPublicKey
                : user?.publicKey
            }
            secretKey={
              showTransferModal?.isAssociated
                ? showTransferModal?.vesselSecretKey
                : privateKey
            }
          />

          <AssociateUserVessel
            toggle={toggleAssociateModal}
            show={showAssociateModal?.show}
            vesselId={showAssociateModal?.vesselId}
            publicKey={user?.publicKey}
            privateKey={privateKey}
          />
          <h1 className="page-title">{t("My vessels")}</h1>
          <Button
            variant="success"
            size="sm"
            style={{
              marginLeft: "15px",
              padding: "2px 10px",
              borderRadius: "5px",
              fontWeight: "bold",
              fontSize: "16px",
            }}
            onClick={() => history.push("/vessels/new")}
          >
            + {t("New")}
          </Button>
          <Table bordered hover style={{ textAlign: "center" }}>
            <thead>
              <tr
                style={{
                  textAlign: "center",
                  backgroundColor: "#b3b2b2",
                  color: "#FFFFFF",
                }}
              >
                <th>{t("Name")}</th>

                <th>{t("Vessel")}</th>

                <th>{t("Type")}</th>

                <th>{t("Stakeholder")}</th>

                <th>{t("CA")}</th>
                <th>{t("Last modified")}</th>

                <th>{t("Actions")}</th>
              </tr>
            </thead>
            {vessels?.map((vessel) => (
              <tr key={vessel?.id}>
                <td>
                  {vessel?.isAssociated
                    ? safeDecrypt(
                        vessel?.sheetName,
                        vessel?.vesselPublicKey,
                        vessel?.vesselSecretKey
                      )
                    : safeDecrypt(
                        vessel?.sheetName,
                        user?.publicKey,
                        privateKey
                      )}
                </td>
                <td>
                  {vessel?.isAssociated
                    ? safeDecrypt(
                        vessel?.vesselName,
                        vessel?.vesselPublicKey,
                        vessel?.vesselSecretKey
                      )
                    : safeDecrypt(
                        vessel?.vesselName,
                        user?.publicKey,
                        privateKey
                      )}
                </td>
                <td>
                  {vessel?.isAssociated
                    ? safeDecrypt(
                        vessel?.vesselType,
                        vessel?.vesselPublicKey,
                        vessel?.vesselSecretKey
                      )
                    : safeDecrypt(
                        vessel?.vesselType,
                        user?.publicKey,
                        privateKey
                      )}
                </td>
                <td>
                  {vessel?.isAssociated
                    ? safeDecrypt(
                        vessel?.vesselStakeholder,
                        vessel?.vesselPublicKey,
                        vessel?.vesselSecretKey
                      )
                    : safeDecrypt(
                        vessel?.vesselStakeholder,
                        user?.publicKey,
                        privateKey
                      )}
                </td>
                <td>
                  {vessel?.isAssociated
                    ? safeDecrypt(
                        vessel?.vesselCentralAgent,
                        vessel?.vesselPublicKey,
                        vessel?.vesselSecretKey
                      )
                    : safeDecrypt(
                        vessel?.vesselCentralAgent,
                        user?.publicKey,
                        privateKey
                      )}
                </td>
                <td>
                  {moment(vessel?.lastUpdate).format("DD/MM/YYYY - HH:mm")}
                </td>
                <td>
                  <OverlayTrigger
                    key="see"
                    placement="top"
                    overlay={<Tooltip id="see">{t("See / Modify")}</Tooltip>}
                  >
                    <Button
                      variant="outline-primary"
                      size="sm"
                      onClick={() => handleEdit(vessel?.id)}
                    >
                      <i className="fa fa-edit"></i>
                    </Button>
                  </OverlayTrigger>
                  <OverlayTrigger
                    key="delete"
                    placement="top"
                    overlay={<Tooltip id="delete">{t("Delete")}</Tooltip>}
                  >
                    <Button
                      variant="outline-primary"
                      size="sm"
                      onClick={() =>
                        toggleDeleteModal(
                          vessel?.id,
                          vessel?.sheetName,
                          vessel?.isAssociated,
                          vessel?.vesselPublicKey,
                          vessel?.vesselSecretKey
                        )
                      }
                    >
                      <i className="fa fa-trash"></i>
                    </Button>
                  </OverlayTrigger>
                  <OverlayTrigger
                    key="duplicate"
                    placement="top"
                    overlay={<Tooltip id="duplicate">{t("Duplicate")}</Tooltip>}
                  >
                    <Button
                      variant="outline-primary"
                      size="sm"
                      onClick={() =>
                        toggleCopyModal(
                          vessel?.id,
                          vessel,
                          vessel?.isAssociated,
                          vessel?.vesselPublicKey,
                          vessel?.vesselSecretKey
                        )
                      }
                    >
                      <i className="fa fa-copy"></i>
                    </Button>
                  </OverlayTrigger>
                  <OverlayTrigger
                    key="transfer"
                    placement="top"
                    overlay={<Tooltip id="transfer">{t("Transfer")}</Tooltip>}
                  >
                    <Button
                      variant="outline-primary"
                      size="sm"
                      onClick={() =>
                        toggleTransferModal(
                          vessel?.id,
                          vessel?.sheetName,
                          vessel?.isAssociated,
                          vessel?.vesselPublicKey,
                          vessel?.vesselSecretKey
                        )
                      }
                    >
                      <i className="fa fa-paper-plane"></i>{" "}
                    </Button>
                  </OverlayTrigger>
                  <OverlayTrigger
                    key="associate"
                    placement="top"
                    overlay={<Tooltip id="associate">{t("Associate")}</Tooltip>}
                  >
                    <Button
                      variant="outline-primary"
                      size="sm"
                      onClick={() => toggleAssociateModal(vessel?.id)}
                    >
                      <i className="fa fa-user-plus"></i>{" "}
                    </Button>
                  </OverlayTrigger>
                </td>
              </tr>
            ))}
          </Table>
        </>
      )}
    </>
  );
}
